import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { Hourglass, CheckCircle, XCircle, Loader2, Search, RefreshCw } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-[#FACC15]/20 text-[#0A2540]",
  approved: "bg-[#10B981]/10 text-[#10B981]",
  rejected: "bg-[#EF4444]/10 text-[#EF4444]",
};
const STATUS_LABELS = { pending: "En attente", approved: "Approuvé", rejected: "Rejeté" };

export default function AdminPackRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [rejectDialog, setRejectDialog] = useState(null);
  const [reason, setReason] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/packs/purchase-requests", { params: filter ? { status: filter } : {} });
      setRequests(data || []);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req) => {
    if (busyId) return;
    setBusyId(req.id);
    try {
      await api.post(`/admin/packs/purchase-requests/${req.id}/approve`);
      toast.success(`Pack ${req.pack_type} activé pour ${req.driver_name}`);
      await load();
    } catch (e) {
      toast.error("Échec", { description: e.response?.data?.detail || "Erreur" });
    } finally { setBusyId(null); }
  };

  const submitReject = async () => {
    if (!rejectDialog) return;
    setBusyId(rejectDialog.id);
    try {
      await api.post(`/admin/packs/purchase-requests/${rejectDialog.id}/reject`, { reason });
      toast.success("Demande rejetée");
      setRejectDialog(null);
      setReason("");
      await load();
    } catch (e) {
      toast.error("Échec", { description: e.response?.data?.detail || "Erreur" });
    } finally { setBusyId(null); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? requests.filter((r) =>
        (r.driver_name || "").toLowerCase().includes(q) ||
        (r.driver_phone || "").toLowerCase().includes(q) ||
        (r.reference || "").toLowerCase().includes(q) ||
        (r.pack_type || "").toLowerCase().includes(q)
      )
    : requests;

  return (
    <div className="space-y-4" data-testid="admin-pack-requests-panel">
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Hourglass className="w-5 h-5 text-[#FACC15]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Demandes de Pack Heures</h2>
            </div>
            <p className="text-sm text-white/70">Validez ou rejetez les achats de pack envoyés par les passagers.</p>
          </div>
          <Button onClick={load} size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="reload-pack-requests-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualiser
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="pack-filter-bar">
        {["pending", "approved", "rejected", ""].map((s) => (
          <Button key={s || "all"} size="sm" onClick={() => setFilter(s)} className={`text-xs ${filter === s ? "bg-[#0A2540] text-white" : "bg-white text-[#0A2540] border border-[#CBD5E1] hover:bg-[#F8FAFC]"}`} data-testid={`pack-filter-${s || "all"}`}>
            {s ? STATUS_LABELS[s] : "Tous"}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#64748B]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (nom, tel, réf, pack)..." className="h-9 pl-7 border-[#CBD5E1]" data-testid="pack-search-input" />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#0A2540] animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#CBD5E1] rounded-xl p-8 text-center" data-testid="no-pack-requests">
          <p className="text-sm text-[#64748B]">Aucune demande {filter ? STATUS_LABELS[filter].toLowerCase() : ""}.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-[#CBD5E1] p-4" data-testid={`pack-request-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-[#FACC15]/20 text-[#0A2540]">Pack {r.pack_type}</span>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLES[r.status]}`}>{STATUS_LABELS[r.status]}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#0A2540]" data-testid={`pack-req-name-${r.id}`}>{r.driver_name}</p>
                  <p className="text-xs text-[#64748B]">{r.driver_phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{(r.pack_price_xaf || 0).toLocaleString()} <span className="text-xs font-normal text-[#64748B]">XAF</span></p>
                  <p className="text-[11px] text-[#64748B]">{r.pack_hours}h · {r.pack_rate_per_hour?.toLocaleString()} F/h</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div><p className="text-[10px] uppercase text-[#64748B]">Opérateur</p><p className="text-[#0A2540] font-medium">{r.operator_label}</p></div>
                <div><p className="text-[10px] uppercase text-[#64748B]">Référence</p><p className="text-[#0A2540] font-mono text-[11px]" data-testid={`pack-req-ref-${r.id}`}>{r.reference}</p></div>
                <div><p className="text-[10px] uppercase text-[#64748B]">Téléphone payeur</p><p className="text-[#0A2540]">{r.phone_number || "—"}</p></div>
                <div><p className="text-[10px] uppercase text-[#64748B]">Soumis</p><p className="text-[#0A2540]">{r.created_at ? new Date(r.created_at).toLocaleString("fr-FR") : "—"}</p></div>
              </div>
              {r.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <Button onClick={() => handleApprove(r)} disabled={busyId === r.id} size="sm" className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid={`approve-pack-${r.id}`}>
                    {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />} Approuver
                  </Button>
                  <Button onClick={() => { setRejectDialog(r); setReason(""); }} disabled={busyId === r.id} size="sm" variant="outline" className="text-[#EF4444] border-[#EF4444] hover:bg-[#EF4444]/5" data-testid={`reject-pack-${r.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
                  </Button>
                </div>
              )}
              {r.status === "rejected" && r.rejection_reason && (
                <p className="mt-2 text-xs text-[#EF4444]"><strong>Motif :</strong> {r.rejection_reason}</p>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!rejectDialog} onOpenChange={(o) => !o && setRejectDialog(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="reject-pack-dialog">
          <DialogHeader><DialogTitle className="text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>Rejeter la demande</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#64748B]">Motif transmis au passager :</p>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: référence introuvable" className="h-9" data-testid="reject-reason-input" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRejectDialog(null)} className="flex-1">Annuler</Button>
              <Button onClick={submitReject} className="flex-1 bg-[#EF4444] text-white hover:bg-[#DC2626]" data-testid="confirm-reject-pack-btn">Confirmer le rejet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
